import { Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import { StyledPageContent } from '../../styles/PageStyles'
import { Controller, useFieldArray, useForm } from 'react-hook-form'
import TextInput from '../../components/Form/Inputs/TextInput'
import { t } from 'i18next'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Project, User, UsersTechnology } from '../../store/types'
import RoleType from '../../store/types/enums/RoleType'
import { useGetUsersQuery } from '../../store/redux/reducers/usersSlice'
import dayjs from 'dayjs'
import { useSelector } from 'react-redux'
import { createErrorMessage } from '../../store/utils'
import { getCurrentUser, getIsLeader, getIsManagement } from '../../store/redux/reducers/currentUserSlice'
import { useGetProjectsQuery } from '../../store/redux/reducers/projectsSlice'
import {
  useCreateDemandMutation,
  useEditDemandMutation,
  useLazyGetDemandByIdQuery,
} from '../../store/redux/reducers/demandSlice'
import { Add, Check } from '@mui/icons-material'
import { useAppDispatch, useNotification } from '../../store/redux/hooks'
import { defaultDemandFormValues, DemandFormValues } from '../../store/types/form/DemandFormValues'
import { useNavigate, useParams } from 'react-router-dom'
import navigation from '../../config/navigation'
import { isEqual } from 'lodash'
import { PageIndex, setPageIndex, setPageName } from '../../store/redux/reducers/pageNameSlice'
import { breadcrumbsItemsActions } from '../../store/redux/reducers/breadcrumbsSlice'
import AutocompleteInput from '../../components/Form/Inputs/AutocompleteInput'
import DateInput from '../../components/Form/Inputs/DateInput'
import TechStackTable from '../../components/Table/TechStackTable'
import { LoadingButton } from '@mui/lab'
import SelectInput, { Option } from '../../components/Form/Inputs/SelectInput'
import DemandState from '../../store/types/enums/DemandState'
import DiscardChangesDialog from '../../components/Dialog/DiscardChangesDialog'
import sortByField from '../../store/utils/sortByField'
import { useEditMode } from '../../store/redux/hooks'

export const demandStateOptions: Option[] = [
  { value: DemandState.PENDING, label: 'page.demands.state.pending' },
  { value: DemandState.INTERNAL, label: 'page.demands.state.internal' },
  { value: DemandState.FOR_EMPLOYMENT, label: 'page.demands.state.for_employment' },
]

const AddDemand = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { displayNotification } = useNotification()
  const theme = useTheme()
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'))
  const currentUser = useSelector(getCurrentUser)
  const isLeader = useSelector(getIsLeader)
  const isManagement = useSelector(getIsManagement)
  const isLeaderAndNotManagement = isLeader && !isManagement
  const [selectedTechnologies, setSelectedTechnologies] = useState<UsersTechnology[]>([])
  const [submitterPage, setSubmitterPage] = useState(0)
  const submitterProps = useCallback(() => {
    return {
      page: submitterPage,
      size: 10,
      'roles.name': `${RoleType.ProjectLeader},${RoleType.Management}`,
    }
  }, [submitterPage])
  const { data: submitters } = useGetUsersQuery(submitterProps())
  const [displayedSubmitters, setDisplayedSubmitters] = useState<User[]>([])
  const { setIsEditMode } = useEditMode()

  const [projectsPage, setProjectsPage] = useState(0)
  const projectProps = useCallback(() => {
    return {
      page: projectsPage,
      size: 10,
      'leaders.leader.id': isLeaderAndNotManagement ? currentUser?.id : undefined,
      operational: true,
    }
  }, [projectsPage, currentUser?.id, isLeaderAndNotManagement])
  const { data: projects } = useGetProjectsQuery(projectProps())
  const [displayedProjects, setDisplayedProjects] = useState<Project[]>([])
  let { id } = useParams()
  const [getDemandById] = useLazyGetDemandByIdQuery()

  useEffect(() => {
    dispatch(!id ? setPageName(t('page.name.demands.addDemand')) : setPageName(t('page.name.editDemand')))
    dispatch(setPageIndex(PageIndex.DEMANDS))
  })

  const { handleSubmit, control, reset, formState, watch } = useForm<DemandFormValues>({
    defaultValues: async () => {
      if (id !== undefined) {
        const result = await getDemandById(id)
        if (result.isSuccess) {
          const data = result.data
          setSelectedTechnologies(data.technologies)

          dispatch(breadcrumbsItemsActions.setInitialState())
          dispatch(breadcrumbsItemsActions.setSelectedItems({ demands: t('page.name.editDemand') }))
          return {
            numberOfPeople: data.numberOfPeople.toString(),
            technologies: sortByField(data.technologies, 'name'),
            project: { value: data.project.id, label: data.project.name },
            startDate: data.startDate ? dayjs(data.startDate).utc(true) : null,
            submitter: {
              value: data.submitter.id,
              label: `${data.submitter.firstName} ${data.submitter.lastName}`,
            },
            state: data.state,
            completed: data.completed,
            description: data.description,
            version: data.version,
            vacancy: data.vacancy,
          }
        }
      }

      dispatch(breadcrumbsItemsActions.setInitialState())
      dispatch(breadcrumbsItemsActions.setSelectedItems({ demands: t('page.name.demands.addDemand') }))
      return defaultDemandFormValues
    },
  })

  const { isSubmitting, isSubmitSuccessful, isDirty } = formState

  const [createDemand] = useCreateDemandMutation()
  const [editDemand] = useEditDemandMutation()

  const handleFormSubmit = async (data: DemandFormValues) => {
    if (!id) {
      handleCreate(data)
    } else {
      handleEdit(data)
    }
  }

  const handleCreate = async (data: DemandFormValues) => {
    try {
      createDemand({
        numberOfPeople: parseInt(data.numberOfPeople),
        technologies: data.technologies,
        projectId: data.project?.value,
        startDate: data.startDate,
        submitterId: data.submitter?.value,
        state: data.state,
        description: isEqual(data.description, '') ? null : data.description,
        completed: data.completed,
      }).unwrap()
      displayNotification({ message: t('page.demands.alert.demandCreated'), type: 'success' })
      setIsEditMode(false)
      reset(data)
      setTimeout(() => navigate(navigation.demands), 0)
    } catch (error) {
      let errorDescription = createErrorMessage(error)
      displayNotification({ message: `${t('page.users.message.error')} ${errorDescription}`, type: 'error' })
    }
  }

  const handleEdit = async (data: DemandFormValues) => {
    if (!id) {
      return
    }
    try {
      editDemand({
        id: id,
        demand: {
          numberOfPeople: parseInt(data.numberOfPeople),
          technologies: data.technologies,
          projectId: data.project?.value,
          startDate: data.startDate,
          submitterId: data.submitter?.value,
          state: data.state,
          description: isEqual(data.description, '') ? null : data.description,
          completed: data.completed,
          version: data.version,
          vacancy: data.vacancy,
        },
      }).unwrap()
      displayNotification({ message: t('page.demands.alert.demandEdited'), type: 'success' })
      setIsEditMode(false)
      reset(data)
      setTimeout(() => navigate(navigation.demands), 0)
    } catch (error) {
      let errorDescription = createErrorMessage(error)
      displayNotification({ message: `${t('message.somethingWentWrong')} ${errorDescription}`, type: 'error' })
    }
  }

  useEffect(() => {
    if (submitters) {
      setDisplayedSubmitters(prev => [...new Set([...prev, ...submitters.content])])
    }
  }, [submitters])

  useEffect(() => {
    if (projects) {
      const filteredProjects = projects.content.filter(project => project.operational)
      setDisplayedProjects(prev => [...new Set([...prev, ...filteredProjects])])
    }
  }, [projects])

  const handleFetchMoreData = useCallback(
    (field: string) => {
      if (field === 'submitter' && !submitters?.last) {
        setSubmitterPage(prev => prev + 1)
      } else if (field === 'project' && !projects?.last) {
        setProjectsPage(prev => prev + 1)
      }
    },
    [submitters, projects]
  )

  const { replace } = useFieldArray({
    name: 'technologies',
    control,
  })

  const replaceArray = (technologies: UsersTechnology[]) => {
    return replace(sortByField(technologies, 'name'))
  }

  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const checkUnsavedChanges = () => {
    return isDirty
  }

  const optionsProjects = useMemo(() => {
    const defProj = formState.defaultValues?.project
    const defaultProject = defProj?.value && defProj?.label ? { value: defProj.value, label: defProj.label } : undefined

    const mappedProjects = displayedProjects.map(project => ({
      value: project.id,
      label: project.name,
    }))

    if (defaultProject && !mappedProjects.some(p => p.value === defaultProject.value)) {
      return [...mappedProjects, defaultProject]
    }

    return mappedProjects
  }, [formState.defaultValues?.project, displayedProjects])

  return (
    <StyledPageContent sx={{ color: 'text.light' }}>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Stack direction={'row'} sx={{ justifyContent: 'space-between', alignItems: 'center', pb: 2 }}>
          <Typography component={'h2'} variant={'h4'}>
            {id ? t('page.demands.editedDemand') : t('page.demands.newDemand')}
          </Typography>
          <LoadingButton
            type="submit"
            variant="contained"
            color="primary"
            startIcon={id ? <Check /> : <Add />}
            disabled={isSubmitting || isSubmitSuccessful}
            loading={isSubmitting || isSubmitSuccessful}
            sx={{
              mb: isDownSm ? 4 : 0,
            }}
          >
            {isDownSm ? t('page.demands.saveDemandShort') : t('page.demands.saveDemand')}
          </LoadingButton>
        </Stack>
        <Stack direction={isDownSm ? 'column' : 'row'} justifyContent={'space-between'} gap={4}>
          <Stack gap={2} width={isDownSm ? '100%' : '50%'}>
            <Typography component={'h3'} variant={'h5'}>
              {t('page.demands.information')}
            </Typography>
            <AutocompleteInput
              options={optionsProjects}
              name={'project'}
              control={control}
              label={t('page.demands.selectProject') + ' *'}
              rules={{
                required: t('page.demands.formErrors.projectRequired'),
              }}
              onSelectBottomScroll={() => handleFetchMoreData('project')}
            />
            <TextInput
              name={'numberOfPeople'}
              required
              control={control}
              numericType={'natural'}
              maxLength={2}
              minValue={1}
              maxValue={50}
              label={t('page.demands.table.header.numberOfPeople')}
              rules={{
                required: t('page.demands.formErrors.numberOfPeopleRequired'),
                min: { value: 1, message: t('page.demands.formErrors.numberOfPeopleMin', { value: 1 }) },
                max: { value: 50, message: t('page.demands.formErrors.numberOfPeopleMax', { value: 50 }) },
              }}
            />
            <DateInput
              name={'startDate'}
              control={control}
              defaultValue={dayjs().utc(true)}
              label={t('components.costSidebar.table.body.startDate')}
            />
            <AutocompleteInput
              options={displayedSubmitters.map(submitter => ({
                value: submitter.id,
                label: `${submitter?.firstName} ${submitter?.lastName}`,
              }))}
              name={'submitter'}
              control={control}
              rules={{
                required: t('page.demands.formErrors.submitterRequired'),
              }}
              label={t('page.demands.selectSubmitter') + ' *'}
              onSelectBottomScroll={() => handleFetchMoreData('submitter')}
            />
            {isManagement && (
              <SelectInput
                options={demandStateOptions}
                name={'state'}
                control={control}
                label={t('page.demands.state.label')}
                rules={{ required: t('page.demands.formErrors.stateRequired') }}
              />
            )}
            <TextInput
              name={'description'}
              control={control}
              label={t('page.demands.table.header.description')}
              maxLength={500}
              rules={{
                maxLength: { value: 500, message: t('page.worklog.form.descriptionLengthError') },
              }}
              multiline
              rows={9}
              showCharCounter
            />
          </Stack>
          <Stack gap={2} width={isDownSm ? '100%' : '50%'}>
            <Typography component={'h3'} variant={'h5'}>
              {t('page.demands.neededTechnologies')}
            </Typography>
            <Controller
              control={control}
              name="technologies"
              render={() => (
                <TechStackTable
                  editable={true}
                  replace={replaceArray}
                  watchedTechnologies={watch('technologies')}
                  userTechnologies={selectedTechnologies}
                  smallDefaultTable
                />
              )}
            />
          </Stack>
        </Stack>
      </form>
      <DiscardChangesDialog
        open={isCancelDialogOpen}
        checkUnsavedChanges={checkUnsavedChanges}
        setIsCancelDialogOpen={setIsCancelDialogOpen}
      />
    </StyledPageContent>
  )
}

export default AddDemand
